import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Collapse,
} from '@mui/material';
import { Cancel, Save, ExpandMore, ExpandLess } from '@mui/icons-material';
import dayjs from 'dayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { updateOrder } from '../api';

const EditOrderModal = ({
  open,
  onClose,
  formData,
  handleFormChange,
  handleSubmit,
  clients = [],
  selectedClientLocations = [],
  handleClientChange,
  handleLocationChange,
  bodegas = [],
  handleBodegaChange,
  products = [],
  handleProductChange,
  handleAddProduct,
  orderProducts = [],
  handleRemoveProduct,
  Cuentas = [],
}) => {
  const [showProducts, setShowProducts] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedBodega, setSelectedBodega] = useState(null);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProductDescriptions, setSelectedProductDescriptions] = useState({});
  const [isClienteEspecial, setIsClienteEspecial] = useState(false);
  const [customAddress, setCustomAddress] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [customState, setCustomState] = useState('');
  const [isPickup, setIsPickup] = useState(false);
  const [isSpecialOrder, setIsSpecialOrder] = useState(false);
  const [appointmentTime, setAppointmentTime] = useState(null);
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [originLocations, setOriginLocations] = useState([]);
  const [selectedOriginClient, setSelectedOriginClient] = useState(null);
  const [selectedOriginLocations, setSelectedOriginLocations] = useState([]);
  const [selectedOriginLocation, setSelectedOriginLocation] = useState(null);
  const [customOrigin, setCustomOrigin] = useState('');

  useEffect(() => {
    if (formData) {
      const pickup = formData.D_R === true;
      setIsPickup(pickup);
      const specialOrder = formData.special === true || formData.isSpecialOrder === true;
      setIsSpecialOrder(specialOrder);
      const isSpecialClient = formData.customer === "CLIENTE ESPECIAL";
      setIsClienteEspecial(isSpecialClient);

      if (isSpecialClient) {
        setCustomAddress(formData.address || '');
        setCustomCity(formData.city || formData.City || '');
        setCustomState(formData.state || formData.State || '');
      }

      if (pickup) {
        const origin = clients.find(c => c.customer === formData.Origin);
        setSelectedOrigin(origin || null);
        if (origin) {
          setOriginLocations(origin.locations || []);
          const location = origin.locations?.find(l => l.address === formData.address);
          setSelectedLocation(location || null);
        }
        const destination = bodegas.find(b => b.nombre === formData.customer);
        setSelectedBodega(destination || null);
      } else {
        const bodega = bodegas.find(b => b.nombre === formData.Origin);
        setSelectedBodega(bodega || null);
        if (!isSpecialClient) {
          const location = selectedClientLocations.find(l => l.address === formData.address);
          setSelectedLocation(location || null);
        }
      }

      if (specialOrder) {
        const originClient = clients.find(c => c.customer === formData.Origin);
        setSelectedOriginClient(originClient || null);
        if (originClient) {
          setSelectedOriginLocations(originClient.locations || []);
          const specialLocation = originClient.locations?.find(
            l => l.address === formData.special_address || l.address === formData.address
          );
          setSelectedOriginLocation(specialLocation || null);
        }
        setCustomOrigin(formData.Origin || '');
      }

      let clientProducts = [];
      if (formData.cuenta_id) {
        clientProducts = products.filter(product => 
          String(product.cuenta) === String(formData.cuenta_id)
        );
      }
      if (clientProducts.length === 0 && formData.cuenta) {
        clientProducts = products.filter(product => 
          String(product.cuenta) === String(formData.cuenta)
        );
      }
      if (clientProducts.length === 0 && formData.cuenta && Cuentas.length > 0) {
        const cuentaObj = Cuentas.find(c => c.nombre === formData.cuenta);
        if (cuentaObj) {
          clientProducts = products.filter(product => 
            String(product.cuenta) === String(cuentaObj.id)
          );
        }
      }
      setFilteredProducts(clientProducts);

      if (formData.Appointment_hr) {
        const timeParts = formData.Appointment_hr.split(':');
        if (timeParts.length >= 2) {
          const hours = parseInt(timeParts[0], 10);
          const minutes = parseInt(timeParts[1], 10);
          const timeValue = dayjs().hour(hours).minute(minutes);
          setAppointmentTime(timeValue);
        }
      }
    }
  }, [formData, bodegas, selectedClientLocations, products, Cuentas, clients]);

  useEffect(() => {
    if (orderProducts && orderProducts.length > 0) {
      const descriptions = {};
      orderProducts.forEach((op, index) => {
        const product = products.find(p => p.id === op.product_id);
        if (product && product.descripcion) {
          descriptions[index] = product.descripcion;
        }
      });
      setSelectedProductDescriptions(descriptions);
    }
  }, [orderProducts, products]);

  const handleClientSelect = (event, newValue) => {
    if (newValue) {
      const isSpecialClient = newValue.customer === "CLIENTE ESPECIAL";
      setIsClienteEspecial(isSpecialClient);
      if (isSpecialClient) {
        setCustomAddress(formData.address || '');
        setCustomCity(formData.city || formData.City || '');
        setCustomState(formData.state || formData.State || '');
      }
    } else {
      setIsClienteEspecial(false);
    }
    handleClientChange(newValue);
  };

  const handleCustomAddressChange = (e) => {
    const { name, value } = e.target;
    switch(name) {
      case 'customAddress':
        setCustomAddress(value);
        handleFormChange({
          target: {
            name: 'address',
            value: value
          }
        });
        break;
      case 'customCity':
        setCustomCity(value);
        handleFormChange({
          target: {
            name: 'city',
            value: value
          }
        });
        break;
      case 'customState':
        setCustomState(value);
        handleFormChange({
          target: {
            name: 'state',
            value: value
          }
        });
        break;
      default:
        break;
    }
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    
    if (isClienteEspecial) {
      const formDataForSubmit = {
        ...formData,
        address: customAddress,
        city: customCity,
        City: customCity,
        state: customState,
        State: customState,
        isCustomAddress: true
      };
      handleSubmit(e, formDataForSubmit);
    } else if (isSpecialOrder) {
      const formDataForSubmit = {
        ...formData,
        special: true,
        isSpecialOrder: true,
        special_address: selectedOriginLocation?.address || formData.special_address || '',
        special_city: selectedOriginLocation?.City || formData.special_city || '',
        special_state: selectedOriginLocation?.State || formData.special_state || '',
        address: formData.address || '',
        City: formData.City || '',
        State: formData.State || '',
        productos: orderProducts
      };
      handleSubmit(e, formDataForSubmit);
    } else if (isPickup) {
      const formDataForSubmit = {
        ...formData,
        City: formData.City || selectedLocation?.City || '',
        State: formData.State || selectedLocation?.State || ''
      };
      handleSubmit(e, formDataForSubmit);
    } else {
      handleSubmit(e);
    }
  };

  const handleLocationSelect = (event, newValue) => {
    setSelectedLocation(newValue);
    if (newValue) {
      handleFormChange({
        target: {
          name: 'address',
          value: newValue.address
        }
      });
      handleFormChange({
        target: {
          name: 'city',
          value: newValue.city || newValue.City || ''
        }
      });
      handleFormChange({
        target: {
          name: 'City', 
          value: newValue.city || newValue.City || ''
        }
      });
      handleFormChange({
        target: {
          name: 'state',
          value: newValue.state || newValue.State || ''
        }
      });
      handleFormChange({
        target: {
          name: 'State',
          value: newValue.state || newValue.State || ''
        }
      });
      handleLocationChange(null, {
        address: newValue.address,
        city: newValue.city || newValue.City || '',
        state: newValue.state || newValue.State || ''
      });
    }
  };

  const handleBodegaSelect = (event, newValue) => {
    setSelectedBodega(newValue);
    if (newValue) {
      handleBodegaChange(event, newValue);
      if (isPickup) {
        handleFormChange({
          target: {
            name: 'customer',
            value: newValue.nombre
          }
        });
      } else {
        handleFormChange({
          target: {
            name: 'Origin',
            value: newValue.nombre
          }
        });
      }
    }
  };

  const handleOriginSelect = (event, newValue) => {
    setSelectedOrigin(newValue);
    if (newValue) {
      handleFormChange({
        target: {
          name: 'Origin',
          value: newValue.customer
        }
      });
      setOriginLocations(newValue.locations || []);
      setSelectedLocation(null);
    } else {
      setOriginLocations([]);
      setSelectedLocation(null);
      handleFormChange({
        target: {
          name: 'Origin',
          value: ''
        }
      });
    }
  };

  const handleProductSelect = (index, newValue) => {
    if (!newValue) {
      handleProductChange(index, null);
      setSelectedProductDescriptions(prev => ({
        ...prev,
        [index]: ''
      }));
      return;
    }
    const isDuplicate = orderProducts.some((op, idx) => 
      idx !== index && 
      op && 
      op.product_id === newValue.id
    );
    if (isDuplicate) {
      return;
    }
    handleProductChange(index, newValue);
    setSelectedProductDescriptions(prev => ({
      ...prev,
      [index]: newValue.descripcion
    }));
  };

  useEffect(() => {
    if (isPickup) {
      const selectedClient = clients.find((client) => client.customer === formData.Origin);
      if (selectedClient) {
        setOriginLocations(selectedClient.locations || []);
      }
    } else {
      const selectedClient = clients.find((client) => client.customer === formData.customer);
      if (selectedClient) {
        setFilteredLocations(selectedClient.locations || []);
      }
    }
  }, [formData.customer, formData.Origin, clients, isPickup]);

  useEffect(() => {
    if (formData.address && filteredLocations.length > 0 && !isClienteEspecial) {
      const locationExists = filteredLocations.some(loc => loc.address === formData.address);
      if (!locationExists) {
        handleLocationChange(null, filteredLocations[0]);
      }
    }
  }, [filteredLocations, formData.address, handleLocationChange, isClienteEspecial]);

  const handleTimeChange = (newTime) => {
    setAppointmentTime(newTime);
    const formattedTime = newTime ? newTime.format('HH:mm') : '';
    handleFormChange({
      target: {
        name: 'Appointment_hr',
        value: formattedTime
      }
    });
  };

  const getDeliveryLabel = () => {
    if (isPickup) {
      return "Recolección";
    } else {
      return "Entrega";
    }
  };

  const handleModalClose = () => {
    setShowProducts(false);
    setSelectedLocation(null);
    setSelectedBodega(null);
    setFilteredLocations([]);
    setFilteredProducts([]);
    setSelectedProductDescriptions({});
    setIsClienteEspecial(false);
    setCustomAddress('');
    setCustomCity('');
    setCustomState('');
    setIsPickup(false);
    setIsSpecialOrder(false);
    setAppointmentTime(null);
    onClose();
  };

  const handleOriginClientSelect = (event, newValue) => {
    setSelectedOriginClient(newValue);
    if (newValue) {
      handleFormChange({
        target: {
          name: 'Origin',
          value: newValue.customer
        }
      });
      setSelectedOriginLocations(newValue.locations || []);
      setSelectedOriginLocation(null);
    } else {
      setSelectedOriginClient(null);
      setSelectedOriginLocations([]);
      setSelectedOriginLocation(null);
    }
  };

  const handleOriginLocationSelect = (event, newValue) => {
    setSelectedOriginLocation(newValue);
    if (newValue) {
      const locationAddress = newValue.address || '';
      const locationCity = newValue.city || newValue.City || '';
      const locationState = newValue.state || newValue.State || '';
      handleFormChange({
        target: {
          name: 'special_address',
          value: locationAddress
        }
      });
      handleFormChange({
        target: {
          name: 'special_city',
          value: locationCity
        }
      });
      handleFormChange({
        target: {
          name: 'special_state',
          value: locationState
        }
      });
    }
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        <Typography variant="h6" gutterBottom>
          {isSpecialOrder 
            ? "Editar Orden Especial" 
            : isPickup 
              ? "Editar Recolección" 
              : "Editar Entrega"}
        </Typography>
        <form onSubmit={handleSubmitForm}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label="DO"
              name="Order_number"
              value={formData.Order_number || ''}
              onChange={handleFormChange}
              required
              disabled
            />
            <TextField
              fullWidth
              label="Customer PO"
              name="Customer_PO"
              value={formData.Customer_PO || ''}
              onChange={handleFormChange}
              required
              disabled
            />
          </Box>
          {isPickup ? (
            <>
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={bodegas}
                  getOptionLabel={(option) => option.nombre || ''}
                  onChange={handleBodegaSelect}
                  value={selectedBodega}
                  renderInput={(params) => <TextField {...params} label="Almacén Destino" required />}
                  disabled={false}
                />
                <Autocomplete
                  fullWidth
                  options={clients}
                  getOptionLabel={(option) => option.customer || ''}
                  onChange={handleOriginSelect}
                  value={selectedOrigin}
                  renderInput={(params) => <TextField {...params} label="Origen de Recolección" required />}
                />
              </Box>
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={originLocations}
                  getOptionLabel={(option) => option.address || ''}
                  onChange={handleLocationSelect}
                  value={selectedLocation}
                  renderInput={(params) => <TextField {...params} label="Dirección de Recolección" required />}
                />
              </Box>
            </>
          ) : isSpecialOrder ? (
            <>
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={clients}
                  getOptionLabel={(option) => option.customer}
                  onChange={handleClientSelect}
                  value={clients.find((client) => client.customer === formData.customer) || null}
                  renderInput={(params) => <TextField {...params} label="Consignatario" required />}
                />
                {isClienteEspecial ? (
                  <TextField
                    fullWidth
                    label="Ubicación (Dirección)"
                    name="customAddress"
                    value={customAddress}
                    onChange={handleCustomAddressChange}
                    required
                  />
                ) : (
                  <Autocomplete
                    fullWidth
                    options={filteredLocations}
                    getOptionLabel={(option) => option.address}
                    onChange={handleLocationSelect}
                    value={selectedLocation}
                    renderInput={(params) => <TextField {...params} label="Ubicación" required />}
                  />
                )}
              </Box>
              {isClienteEspecial && (
                <Box display="flex" gap={2} mt={2}>
                  <TextField
                    fullWidth
                    label="Ciudad"
                    name="customCity"
                    value={customCity}
                    onChange={handleCustomAddressChange}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Estado"
                    name="customState"
                    value={customState}
                    onChange={handleCustomAddressChange}
                    required
                  />
                </Box>
              )}
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={clients}
                  getOptionLabel={(option) => option.customer}
                  onChange={handleOriginClientSelect}
                  value={selectedOriginClient}
                  renderInput={(params) => <TextField {...params} label="Origen de Carga" required />}
                />
              </Box>
              {selectedOriginClient && (
                <Box display="flex" gap={2} mt={2}>
                  <Autocomplete
                    fullWidth
                    options={selectedOriginLocations}
                    getOptionLabel={(option) => option.address}
                    onChange={handleOriginLocationSelect}
                    value={selectedOriginLocation}
                    renderInput={(params) => <TextField {...params} label="Ubicación de Origen" required />}
                  />
                </Box>
              )}
            </>
          ) : isClienteEspecial ? (
            <>
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={clients}
                  getOptionLabel={(option) => option.customer}
                  onChange={handleClientSelect}
                  value={clients.find((client) => client.customer === formData.customer) || null}
                  renderInput={(params) => <TextField {...params} label="Cliente" required />}
                  disabled
                />
                {isClienteEspecial ? (
                  <TextField
                    fullWidth
                    label="Ubicación (Dirección)"
                    name="customAddress"
                    value={customAddress}
                    onChange={handleCustomAddressChange}
                    required
                  />
                ) : (
                  <Autocomplete
                    fullWidth
                    options={filteredLocations}
                    getOptionLabel={(option) => option.address}
                    onChange={handleLocationSelect}
                    value={selectedLocation}
                    renderInput={(params) => <TextField {...params} label="Ubicación" required />}
                    disableClearable
                  />
                )}
              </Box>
              {isClienteEspecial && (
                <Box display="flex" gap={2} mt={2}>
                  <TextField
                    fullWidth
                    label="Ciudad"
                    name="customCity"
                    value={customCity}
                    onChange={handleCustomAddressChange}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Estado"
                    name="customState"
                    value={customState}
                    onChange={handleCustomAddressChange}
                    required
                  />
                </Box>
              )}
            </>
          ) : (
            <>
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={clients}
                  getOptionLabel={(option) => option.customer}
                  onChange={handleClientSelect}
                  value={clients.find((client) => client.customer === formData.customer) || null}
                  renderInput={(params) => <TextField {...params} label="Cliente" required />}
                  disabled
                />
                {isClienteEspecial ? (
                  <TextField
                    fullWidth
                    label="Ubicación (Dirección)"
                    name="customAddress"
                    value={customAddress}
                    onChange={handleCustomAddressChange}
                    required
                  />
                ) : (
                  <Autocomplete
                    fullWidth
                    options={filteredLocations}
                    getOptionLabel={(option) => option.address}
                    onChange={handleLocationSelect}
                    value={selectedLocation}
                    renderInput={(params) => <TextField {...params} label="Ubicación" required />}
                    disableClearable
                  />
                )}
              </Box>
              {isClienteEspecial && (
                <Box display="flex" gap={2} mt={2}>
                  <TextField
                    fullWidth
                    label="Ciudad"
                    name="customCity"
                    value={customCity}
                    onChange={handleCustomAddressChange}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Estado"
                    name="customState"
                    value={customState}
                    onChange={handleCustomAddressChange}
                    required
                  />
                </Box>
              )}
              <Box display="flex" gap={2} mt={2}>
                <Autocomplete
                  fullWidth
                  options={bodegas}
                  getOptionLabel={(option) => option.nombre}
                  onChange={handleBodegaSelect}
                  value={selectedBodega}
                  renderInput={(params) => <TextField {...params} label="Almacen" required />}
                />
              </Box>
            </>
          )}
          <Box display="flex" gap={2} mt={2}>
            <TextField
              fullWidth
              label={`Fecha de ${getDeliveryLabel()}`}
              type="date"
              name="Appointment"
              value={formData.Appointment || ''}
              onChange={handleFormChange}
              required
              InputLabelProps={{ shrink: true }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label={`Hora de ${getDeliveryLabel()}`}
                value={appointmentTime}
                onChange={handleTimeChange}
                ampm={false}
                minutesStep={5}
                className="full-width"
                slotProps={{ 
                  textField: { 
                    fullWidth: true,
                    required: true
                  } 
                }}
                format="HH:mm"
              />
            </LocalizationProvider>
          </Box>
          <Button
            startIcon={showProducts ? <ExpandLess /> : <ExpandMore />}
            fullWidth
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => setShowProducts(!showProducts)}
          >
            {showProducts ? 'Ocultar Productos' : 'Editar Productos'}
          </Button>
          <Collapse in={showProducts} sx={{ mt: 2 }}>
            {orderProducts.map((orderProduct, index) => (
              <Box key={index} display="flex" flexDirection="column" gap={1} mt={2} p={2} sx={{ border: '1px solid #ccc', borderRadius: '4px' }}>
                <Box display="flex" gap={2}>
                  <Autocomplete
                    fullWidth
                    options={filteredProducts.length > 0 ? filteredProducts : []}
                    getOptionLabel={(option) => option.codigo_producto || ''}
                    onChange={(event, value) => handleProductSelect(index, value)}
                    value={products.find((product) => product.id === orderProduct.product_id) || null}
                    renderInput={(params) => <TextField {...params} label="Producto" required />}
                    noOptionsText="No hay productos disponibles para este cliente"
                  />
                  <TextField
                    type="number"
                    label="Cantidad"
                    fullWidth
                    value={orderProduct.product_qt || ''}
                    onChange={(e) => handleProductChange(index, e.target.value, 'product_qt')}
                    inputProps={{ min: 0 }}
                  />
                  <Button onClick={() => handleRemoveProduct(index)} color="error">
                    Eliminar
                  </Button>
                </Box>
                {selectedProductDescriptions[index] && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    <strong>Descripción:</strong> {selectedProductDescriptions[index]}
                  </Typography>
                )}
              </Box>
            ))}
            <Button
              variant="outlined"
              fullWidth
              onClick={handleAddProduct}
              sx={{ mt: 2 }}
              disabled={filteredProducts.length === 0}
            >
              {filteredProducts.length === 0 ? 'No hay productos disponibles para este cliente' : 'Agregar Producto'}
            </Button>
          </Collapse>
          <Box display="flex" gap={2} mt={2}>
            <Button
              startIcon={<Cancel />}
              variant="outlined"
              fullWidth
              onClick={handleModalClose}
            >
              Cancelar
            </Button>
            <Button
              startIcon={<Save />}
              variant="contained"
              fullWidth
              type="submit"
            >
              Guardar
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default EditOrderModal;
